const PLAYER_SELECTOR = '[data-df-output-audio-player]';
const PLAY_ICON = 'fa-play';
const PAUSE_ICON = 'fa-pause';
let activeTrack = null;
let audioOutputPlayerObserver = null;
let audioOutputPlayerObserverRoot = null;

const formatTime = (seconds) => {
    const safeSeconds = Number.isFinite(seconds) ? Math.max(seconds, 0) : 0;
    const hours = Math.floor(safeSeconds / 3600);
    const minutes = Math.floor((safeSeconds % 3600) / 60);
    const remainingSeconds = Math.floor(safeSeconds % 60);
    const paddedSeconds = String(remainingSeconds).padStart(2, '0');

    if (hours > 0) {
        return `${hours}:${String(minutes).padStart(2, '0')}:${paddedSeconds}`;
    }

    return `${String(minutes).padStart(2, '0')}:${paddedSeconds}`;
};

const updatePlayer = (player) => {
    const audio = player.querySelector('audio');
    const progress = player.querySelector('.df-output-audio-player__progress');
    const track = player.querySelector('.df-output-audio-player__track');
    const currentTime = player.querySelector('.df-output-audio-player__time--current');
    const remainingTime = player.querySelector('.df-output-audio-player__time--remaining');

    if (!audio || !progress || !track || !currentTime || !remainingTime) return;

    const duration = Number.isFinite(audio.duration) ? audio.duration : 0;
    const current = Number.isFinite(audio.currentTime) ? audio.currentTime : 0;
    const percentage = duration > 0 ? Math.min((current / duration) * 100, 100) : 0;

    progress.style.width = `${percentage}%`;
    track.setAttribute('aria-valuenow', Math.round(percentage));
    track.setAttribute('aria-valuetext', `${formatTime(current)} / ${formatTime(duration)}`);
    currentTime.textContent = formatTime(current);
    remainingTime.textContent = `-${formatTime(Math.max(duration - current, 0))}`;
};

const setPlaying = (player, isPlaying) => {
    const button = player.querySelector('.df-output-audio-player__toggle');

    if (!button) return;

    const icon = button.querySelector('i');

    player.classList.toggle('is-playing', isPlaying);
    button.setAttribute('aria-pressed', isPlaying ? 'true' : 'false');
    button.setAttribute('aria-label', isPlaying ? 'Pause' : 'Play');
    if (icon) {
        icon.classList.toggle(PLAY_ICON, !isPlaying);
        icon.classList.toggle(PAUSE_ICON, isPlaying);
    }
};

const initializePlayer = (player) => {
    if (player.dataset.dfOutputAudioPlayerReady) return;
    player.dataset.dfOutputAudioPlayerReady = 'true';

    const audio = player.querySelector('audio');
    if (!audio) return;

    audio.loop = false;
    audio.addEventListener('loadedmetadata', () => updatePlayer(player));
    audio.addEventListener('timeupdate', () => updatePlayer(player));
    audio.addEventListener('play', () => setPlaying(player, true));
    audio.addEventListener('pause', () => setPlaying(player, false));
    audio.addEventListener('ended', () => {
        audio.currentTime = 0;
        setPlaying(player, false);
        updatePlayer(player);
    });

    updatePlayer(player);
};

const togglePlayer = (button) => {
    const player = button.closest(PLAYER_SELECTOR);
    if (!player) return;

    initializePlayer(player);

    const audio = player.querySelector('audio');
    if (!audio) return;

    if (audio.paused) {
        document.querySelectorAll(`${PLAYER_SELECTOR} audio`).forEach((otherAudio) => {
            if (otherAudio !== audio) otherAudio.pause();
        });

        const playPromise = audio.play();
        if (playPromise && typeof playPromise.catch === 'function') {
            playPromise.catch(() => setPlaying(player, false));
        }
    } else {
        audio.pause();
    }
};

const seekPlayer = (track, clientX) => {
    const player = track.closest(PLAYER_SELECTOR);
    if (!player) return;

    initializePlayer(player);

    const audio = player.querySelector('audio');
    if (!audio || !Number.isFinite(audio.duration) || audio.duration <= 0) return;

    const trackBounds = track.getBoundingClientRect();
    const percentage = Math.min(Math.max((clientX - trackBounds.left) / trackBounds.width, 0), 1);

    audio.currentTime = audio.duration * percentage;
    updatePlayer(player);
};

const stepPlayer = (track, direction) => {
    const player = track.closest(PLAYER_SELECTOR);
    if (!player) return;

    initializePlayer(player);

    const audio = player.querySelector('audio');
    if (!audio || !Number.isFinite(audio.duration) || audio.duration <= 0) return;

    audio.currentTime = Math.min(Math.max(audio.currentTime + direction * 5, 0), audio.duration);
    updatePlayer(player);
};

const bindAudioOutputPlayerEvents = () => {
    if (window.dfOutputAudioPlayerEventsBound) return;
    window.dfOutputAudioPlayerEventsBound = true;

    document.addEventListener('click', (event) => {
        const button = event.target.closest('.df-output-audio-player__toggle');
        if (button) togglePlayer(button);
    });

    document.addEventListener('pointerdown', (event) => {
        const track = event.target.closest('.df-output-audio-player__track');
        if (!track) return;

        activeTrack = track;
        seekPlayer(track, event.clientX);
    });

    document.addEventListener('pointermove', (event) => {
        if (activeTrack) seekPlayer(activeTrack, event.clientX);
    });

    document.addEventListener('pointerup', () => {
        activeTrack = null;
    });

    document.addEventListener('pointercancel', () => {
        activeTrack = null;
    });

    document.addEventListener('keydown', (event) => {
        const track = event.target.closest('.df-output-audio-player__track');
        if (!track) return;

        if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') {
            event.preventDefault();
            stepPlayer(track, -1);
        }

        if (event.key === 'ArrowRight' || event.key === 'ArrowUp') {
            event.preventDefault();
            stepPlayer(track, 1);
        }
    });
};

const initializeAudioOutputPlayers = () => {
    bindAudioOutputPlayerEvents();
    document.querySelectorAll(PLAYER_SELECTOR).forEach(initializePlayer);
};

const observeAudioOutputPlayerInserts = () => {
    if (!document.body) return;

    if (audioOutputPlayerObserver && audioOutputPlayerObserverRoot === document.body) return;

    if (audioOutputPlayerObserver) {
        audioOutputPlayerObserver.disconnect();
    }

    audioOutputPlayerObserver = new MutationObserver((mutations) => {
        const hasInsertedPlayer = mutations.some((mutation) => Array.from(mutation.addedNodes).some((node) => (
            node.nodeType === Node.ELEMENT_NODE
            && (node.matches?.(PLAYER_SELECTOR) || node.querySelector?.(PLAYER_SELECTOR))
        )));

        if (hasInsertedPlayer) initializeAudioOutputPlayers();
    });

    audioOutputPlayerObserverRoot = document.body;
    audioOutputPlayerObserver.observe(document.body, { childList: true, subtree: true });
};

const refreshAudioOutputPlayers = () => {
    initializeAudioOutputPlayers();
    observeAudioOutputPlayerInserts();
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refreshAudioOutputPlayers);
} else {
    refreshAudioOutputPlayers();
}

document.addEventListener('turbolinks:load', refreshAudioOutputPlayers);
document.addEventListener('ajax:success', refreshAudioOutputPlayers);
document.addEventListener('ajax:complete', refreshAudioOutputPlayers);

window.initializeAudioOutputPlayers = refreshAudioOutputPlayers;
const PLAYER_SELECTOR = '[data-df-output-audio-player]';
const PLAY_ICON = 'fa-play';
const PAUSE_ICON = 'fa-pause';
let activeTrack = null;
let audioOutputPlayerObserver = null;
let audioOutputPlayerObserverRoot = null;

const formatTime = (seconds) => {
    const safeSeconds = Number.isFinite(seconds) ? Math.max(seconds, 0) : 0;
    const hours = Math.floor(safeSeconds / 3600);
    const minutes = Math.floor((safeSeconds % 3600) / 60);
    const remainingSeconds = Math.floor(safeSeconds % 60);
    const paddedSeconds = String(remainingSeconds).padStart(2, '0');

    if (hours > 0) {
        return `${hours}:${String(minutes).padStart(2, '0')}:${paddedSeconds}`;
    }

    return `${String(minutes).padStart(2, '0')}:${paddedSeconds}`;
};

const updatePlayer = (player) => {
    const audio = player.querySelector('audio');
    const progress = player.querySelector('.df-output-audio-player__progress');
    const track = player.querySelector('.df-output-audio-player__track');
    const currentTime = player.querySelector('.df-output-audio-player__time--current');
    const remainingTime = player.querySelector('.df-output-audio-player__time--remaining');

    if (!audio || !progress || !track || !currentTime || !remainingTime) return;

    const duration = Number.isFinite(audio.duration) ? audio.duration : 0;
    const current = Number.isFinite(audio.currentTime) ? audio.currentTime : 0;
    const percentage = duration > 0 ? Math.min((current / duration) * 100, 100) : 0;

    progress.style.width = `${percentage}%`;
    track.setAttribute('aria-valuenow', Math.round(percentage));
    track.setAttribute('aria-valuetext', `${formatTime(current)} / ${formatTime(duration)}`);
    currentTime.textContent = formatTime(current);
    remainingTime.textContent = `-${formatTime(Math.max(duration - current, 0))}`;
};

const setPlaying = (player, isPlaying) => {
    const button = player.querySelector('.df-output-audio-player__toggle');

    if (!button) return;

    const icon = button.querySelector('i');

    player.classList.toggle('is-playing', isPlaying);
    button.setAttribute('aria-pressed', isPlaying ? 'true' : 'false');
    button.setAttribute('aria-label', isPlaying ? 'Pause' : 'Play');
    if (icon) {
        icon.classList.toggle(PLAY_ICON, !isPlaying);
        icon.classList.toggle(PAUSE_ICON, isPlaying);
    }
};

const initializePlayer = (player) => {
    if (player.dataset.dfOutputAudioPlayerReady) return;
    player.dataset.dfOutputAudioPlayerReady = 'true';

    const audio = player.querySelector('audio');
    if (!audio) return;

    audio.loop = false;
    audio.addEventListener('loadedmetadata', () => updatePlayer(player));
    audio.addEventListener('timeupdate', () => updatePlayer(player));
    audio.addEventListener('play', () => setPlaying(player, true));
    audio.addEventListener('pause', () => setPlaying(player, false));
    audio.addEventListener('ended', () => {
        audio.currentTime = 0;
        setPlaying(player, false);
        updatePlayer(player);
    });

    updatePlayer(player);
};

const togglePlayer = (button) => {
    const player = button.closest(PLAYER_SELECTOR);
    if (!player) return;

    initializePlayer(player);

    const audio = player.querySelector('audio');
    if (!audio) return;

    if (audio.paused) {
        document.querySelectorAll(`${PLAYER_SELECTOR} audio`).forEach((otherAudio) => {
            if (otherAudio !== audio) otherAudio.pause();
        });

        const playPromise = audio.play();
        if (playPromise && typeof playPromise.catch === 'function') {
            playPromise.catch(() => setPlaying(player, false));
        }
    } else {
        audio.pause();
    }
};

const seekPlayer = (track, clientX) => {
    const player = track.closest(PLAYER_SELECTOR);
    if (!player) return;

    initializePlayer(player);

    const audio = player.querySelector('audio');
    if (!audio || !Number.isFinite(audio.duration) || audio.duration <= 0) return;

    const trackBounds = track.getBoundingClientRect();
    const percentage = Math.min(Math.max((clientX - trackBounds.left) / trackBounds.width, 0), 1);

    audio.currentTime = audio.duration * percentage;
    updatePlayer(player);
};

const stepPlayer = (track, direction) => {
    const player = track.closest(PLAYER_SELECTOR);
    if (!player) return;

    initializePlayer(player);

    const audio = player.querySelector('audio');
    if (!audio || !Number.isFinite(audio.duration) || audio.duration <= 0) return;

    audio.currentTime = Math.min(Math.max(audio.currentTime + direction * 5, 0), audio.duration);
    updatePlayer(player);
};

const bindAudioOutputPlayerEvents = () => {
    if (window.dfOutputAudioPlayerEventsBound) return;
    window.dfOutputAudioPlayerEventsBound = true;

    document.addEventListener('click', (event) => {
        const button = event.target.closest('.df-output-audio-player__toggle');
        if (button) togglePlayer(button);
    });

    document.addEventListener('pointerdown', (event) => {
        const track = event.target.closest('.df-output-audio-player__track');
        if (!track) return;

        activeTrack = track;
        seekPlayer(track, event.clientX);
    });

    document.addEventListener('pointermove', (event) => {
        if (activeTrack) seekPlayer(activeTrack, event.clientX);
    });

    document.addEventListener('pointerup', () => {
        activeTrack = null;
    });

    document.addEventListener('pointercancel', () => {
        activeTrack = null;
    });

    document.addEventListener('keydown', (event) => {
        const track = event.target.closest('.df-output-audio-player__track');
        if (!track) return;

        if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') {
            event.preventDefault();
            stepPlayer(track, -1);
        }

        if (event.key === 'ArrowRight' || event.key === 'ArrowUp') {
            event.preventDefault();
            stepPlayer(track, 1);
        }
    });
};

const initializeAudioOutputPlayers = () => {
    bindAudioOutputPlayerEvents();
    document.querySelectorAll(PLAYER_SELECTOR).forEach(initializePlayer);
};

const observeAudioOutputPlayerInserts = () => {
    if (!document.body) return;

    if (audioOutputPlayerObserver && audioOutputPlayerObserverRoot === document.body) return;

    if (audioOutputPlayerObserver) {
        audioOutputPlayerObserver.disconnect();
    }

    audioOutputPlayerObserver = new MutationObserver((mutations) => {
        const hasInsertedPlayer = mutations.some((mutation) => Array.from(mutation.addedNodes).some((node) => (
            node.nodeType === Node.ELEMENT_NODE
            && (node.matches?.(PLAYER_SELECTOR) || node.querySelector?.(PLAYER_SELECTOR))
        )));

        if (hasInsertedPlayer) initializeAudioOutputPlayers();
    });

    audioOutputPlayerObserverRoot = document.body;
    audioOutputPlayerObserver.observe(document.body, { childList: true, subtree: true });
};

const refreshAudioOutputPlayers = () => {
    initializeAudioOutputPlayers();
    observeAudioOutputPlayerInserts();
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refreshAudioOutputPlayers);
} else {
    refreshAudioOutputPlayers();
}

document.addEventListener('turbolinks:load', refreshAudioOutputPlayers);
document.addEventListener('ajax:success', refreshAudioOutputPlayers);
document.addEventListener('ajax:complete', refreshAudioOutputPlayers);

window.initializeAudioOutputPlayers = refreshAudioOutputPlayers;
